<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const i18nScope = 'components.sales_analytics';

  const props = defineProps({
    overview: {
      type: Object,
      required: true
    },
    comparison: {
      type: Object,
      required: true
    },
    libraryName: {
      type: String,
      required: true
    }
  });

  const metrics = computed(() => {
    const result = [];

    // Total purchases
    result.push({
      label: t(`${i18nScope}.total_purchases`),
      yourValue: props.overview.total_purchases || 0,
      peerValue: props.comparison.avg_purchases_per_library || 0,
      format: 'number'
    });

    // Total loans
    result.push({
      label: t(`${i18nScope}.total_loans`),
      yourValue: props.overview.total_loans || 0,
      peerValue: props.comparison.avg_loans_per_library || 0,
      format: 'number'
    });

    // Loans per copy
    const yourLoansPerCopy = props.overview.total_purchases > 0
      ? (props.overview.total_loans / props.overview.total_purchases)
      : 0;
    const peerLoansPerCopy = props.comparison.avg_purchases_per_library > 0
      ? (props.comparison.avg_loans_per_library / props.comparison.avg_purchases_per_library)
      : 0;

    result.push({
      label: t(`${i18nScope}.loans_per_copy`),
      yourValue: yourLoansPerCopy,
      peerValue: peerLoansPerCopy,
      format: 'decimal'
    });

    return result;
  });

  function formatValue(value, format) {
    switch (format) {
      case 'number':
        return Math.round(value).toLocaleString();
      case 'decimal':
        return value.toFixed(1);
      case 'currency':
        return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      default:
        return value;
    }
  }

  function calculateDifference(yourValue, peerValue) {
    if (peerValue === 0) return { value: 0, percentage: 0, class: 'text-muted' };

    const diff = yourValue - peerValue;
    const pct = (diff / peerValue) * 100;

    return {
      value: diff,
      percentage: pct,
      class: pct > 0 ? 'text-success' : pct < 0 ? 'text-danger' : 'text-muted'
    };
  }

  function formatPercentage(pct) {
    const sign = pct > 0 ? '+' : '';
    return sign + pct.toFixed(0) + '%';
  }
</script>

<template>
  <div class="comparison-table">
    <p class="text-muted mb-3">
      {{ t(`${i18nScope}.comparing_to`, { count: comparison.library_count, label: comparison.comparison_label }) }}
    </p>

    <table class="table table-bordered">
      <thead class="table-light">
        <tr>
          <th>{{ t(`${i18nScope}.metric`) }}</th>
          <th class="text-end">{{ libraryName }}</th>
          <th class="text-end">{{ t(`${i18nScope}.peer_average`) }}</th>
          <th class="text-end">{{ t(`${i18nScope}.difference`) }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="metric in metrics" :key="metric.label">
          <td>{{ metric.label }}</td>
          <td class="text-end fw-bold">{{ formatValue(metric.yourValue, metric.format) }}</td>
          <td class="text-end">{{ formatValue(metric.peerValue, metric.format) }}</td>
          <td class="text-end" :class="calculateDifference(metric.yourValue, metric.peerValue).class">
            {{ formatPercentage(calculateDifference(metric.yourValue, metric.peerValue).percentage) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
  .comparison-table .table {
    margin-bottom: 0;
  }

  .comparison-table th,
  .comparison-table td {
    vertical-align: middle;
  }
</style>
